import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ThemeSettings {
  mode: 'auto' | 'light' | 'dark';
  primaryColor: string;
  accentColor: string;
  customColors: {
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BrowserThemeService {
  private currentTheme = new BehaviorSubject<ThemeSettings>({
    mode: 'auto',
    primaryColor: '#3b82f6',
    accentColor: '#10b981',
    customColors: {
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1e293b',
      textSecondary: '#64748b'
    }
  });

  public theme$ = this.currentTheme.asObservable();

  constructor() {
    // Only initialize in browser
    if (this.isBrowser()) {
      this.loadThemeFromStorage();
      this.applyTheme();
    }
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  getCurrentTheme(): ThemeSettings {
    return this.currentTheme.value;
  }

  setTheme(theme: ThemeSettings): void {
    this.currentTheme.next(theme);
    if (this.isBrowser()) {
      this.saveThemeToStorage(theme);
      this.applyTheme();
    }
  }

  setThemeMode(mode: 'auto' | 'light' | 'dark'): void {
    const currentTheme = this.getCurrentTheme();
    this.setTheme({ ...currentTheme, mode });
  }

  setPrimaryColor(color: string): void {
    const currentTheme = this.getCurrentTheme();
    this.setTheme({ ...currentTheme, primaryColor: color });
  }

  setAccentColor(color: string): void {
    const currentTheme = this.getCurrentTheme();
    this.setTheme({ ...currentTheme, accentColor: color });
  }

  toggleDarkMode(): void {
    const currentTheme = this.getCurrentTheme();
    const newMode = currentTheme.mode === 'dark' ? 'light' : 'dark';
    this.setThemeMode(newMode);
  }

  private loadThemeFromStorage(): void {
    if (!this.isBrowser()) return;
    
    try {
      const savedTheme = localStorage.getItem('app_theme');
      if (savedTheme) {
        const theme = JSON.parse(savedTheme);
        this.currentTheme.next(theme);
      }
    } catch (error) {
      console.error('Error loading theme from storage:', error);
    }
  }

  private saveThemeToStorage(theme: ThemeSettings): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.setItem('app_theme', JSON.stringify(theme));
    } catch (error) {
      console.error('Error saving theme to storage:', error);
    }
  }

  private applyTheme(): void {
    if (!this.isBrowser()) return;
    
    try {
      const theme = this.getCurrentTheme();
      const root = document.documentElement;

      // Apply theme mode
      this.applyThemeMode(root, theme.mode);
      
      // Apply colors
      this.applyColors(root, theme);
      
      // Apply CSS custom properties
      this.applyCSSVariables(root, theme);
      
      } catch (error) {
      console.error('Error applying theme:', error);
    }
  }

  private applyThemeMode(root: HTMLElement, mode: string): void {
    try {
      // Remove existing theme classes
      root.classList.remove('light', 'dark');
      
      if (mode === 'dark') {
        root.classList.add('dark');
      } else if (mode === 'light') {
        root.classList.add('light');
      } else {
        // Auto mode - follow system preference
        if (typeof window.matchMedia !== 'undefined') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (prefersDark) {
            root.classList.add('dark');
          } else {
            root.classList.add('light');
          }
        } else {
          root.classList.add('light');
        }
      }
    } catch (error) {
      console.error('Error applying theme mode:', error);
      root.classList.add('light');
    }
  }

  private applyColors(root: HTMLElement, theme: ThemeSettings): void {
    try {
      // Apply primary and accent colors
      root.style.setProperty('--primary-color', theme.primaryColor);
      root.style.setProperty('--accent-color', theme.accentColor);
      
      // Apply custom colors based on theme mode
      const isDark = root.classList.contains('dark');
      if (isDark) {
        root.style.setProperty('--background-color', '#1e293b');
        root.style.setProperty('--surface-color', '#334155');
        root.style.setProperty('--text-color', '#f1f5f9');
        root.style.setProperty('--text-secondary-color', '#94a3b8');
      } else {
        root.style.setProperty('--background-color', theme.customColors.background);
        root.style.setProperty('--surface-color', theme.customColors.surface);
        root.style.setProperty('--text-color', theme.customColors.text);
        root.style.setProperty('--text-secondary-color', theme.customColors.textSecondary);
      }
    } catch (error) {
      console.error('Error applying colors:', error);
    }
  }

  private applyCSSVariables(root: HTMLElement, theme: ThemeSettings): void {
    try {
      // Apply additional CSS variables for consistency
      root.style.setProperty('--primary-50', this.lightenColor(theme.primaryColor, 0.95));
      root.style.setProperty('--primary-100', this.lightenColor(theme.primaryColor, 0.9));
      root.style.setProperty('--primary-500', theme.primaryColor);
      root.style.setProperty('--primary-600', this.darkenColor(theme.primaryColor, 0.1));
      root.style.setProperty('--primary-700', this.darkenColor(theme.primaryColor, 0.2));
      
      root.style.setProperty('--accent-50', this.lightenColor(theme.accentColor, 0.95));
      root.style.setProperty('--accent-100', this.lightenColor(theme.accentColor, 0.9));
      root.style.setProperty('--accent-500', theme.accentColor);
      root.style.setProperty('--accent-600', this.darkenColor(theme.accentColor, 0.1));
      root.style.setProperty('--accent-700', this.darkenColor(theme.accentColor, 0.2));
    } catch (error) {
      console.error('Error applying CSS variables:', error);
    }
  }

  private lightenColor(color: string, amount: number): string {
    try {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      const newR = Math.round(r + (255 - r) * amount);
      const newG = Math.round(g + (255 - g) * amount);
      const newB = Math.round(b + (255 - b) * amount);
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    } catch (error) {
      console.error('Error lightening color:', error);
      return color;
    }
  }

  private darkenColor(color: string, amount: number): string {
    try {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      const newR = Math.round(r * (1 - amount));
      const newG = Math.round(g * (1 - amount));
      const newB = Math.round(b * (1 - amount));
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    } catch (error) {
      console.error('Error darkening color:', error);
      return color;
    }
  }

  // Listen for system theme changes
  listenForSystemThemeChanges(): void {
    if (!this.isBrowser() || typeof window.matchMedia === 'undefined') return;
    
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', () => {
        const currentTheme = this.getCurrentTheme();
        if (currentTheme.mode === 'auto') {
          this.applyTheme();
        }
      });
    } catch (error) {
      console.error('Error setting up system theme listener:', error);
    }
  }
}
